import styles from './MarketHealth.module.css';

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

/**
 * Compact price-gate pipeline indicator placed between ModelState and the
 * factor breakdown. Shows how far SPY sits from the strategy's trigger level
 * and, when relevant, confirmation-day progress toward a trade.
 *
 * Returns null when `triggerInfo` is absent (backend hasn't deployed yet or
 * older payload) so the layout degrades gracefully.
 *
 * @param {object|null|undefined} triggerInfo - per-strategy slice from payload.current.triggerInfo[mode]
 * @param {string} mode - current strategy key (used for conservative-specific copy)
 * @param {number|null} score - current canonical risk score (for threshold comparison)
 * @param {string} position - 'invested' | 'cash'
 * @param {object} config - STRATEGY_CONFIGS[mode] (riskBoundary, scoreDomain, etc.)
 */
// ── Proximity bar ──────────────────────────────────────────────────────
// A tiny inline meter that shows how close SPY is to the trigger without
// revealing the actual percentage. Clamped to a visual range of -5% to +15%
// so the bar has meaningful movement in the typical operating range.
const PROX_MIN = -5;   // fully past trigger (red)
const PROX_MAX = 15;   // far above trigger (green/empty)
const PROX_SPAN = PROX_MAX - PROX_MIN;

// Map distancePct to a color: green when safe, amber approaching trigger, red when past.
const proximityColor = (dist) => {
  if (dist <= 0) return '#dc3545';   // past trigger — red
  if (dist <= 3) return '#e0a800';   // close — amber
  return '#28a745';                   // safe — green
};

const ProximityBar = ({ distancePct: dist }) => {
  // Fill grows from the LEFT (danger) toward the trigger tick. When SPY is far
  // above the trigger the fill is small (safe). As SPY drops, the fill grows.
  // Past 0 (trigger active) the fill passes the tick mark.
  const clamped = Math.max(PROX_MIN, Math.min(PROX_MAX, dist));
  // Invert: 0% fill = far above trigger, 100% fill = far below trigger.
  const fillPct = ((PROX_MAX - clamped) / PROX_SPAN) * 100;
  // Tick position: where distancePct === 0 sits on the bar.
  const tickPct = ((PROX_MAX - 0) / PROX_SPAN) * 100;
  const color = proximityColor(dist);

  return (
    <span
      className={styles.proximityBar}
      role="img"
      aria-label={dist > 0 ? 'Price gate: safe distance' : 'Price gate: active'}
    >
      <span className={styles.proximityFill} style={{ width: `${fillPct}%`, backgroundColor: color }} />
      <span className={styles.proximityTick} style={{ left: `${tickPct}%` }} />
    </span>
  );
};

const TriggerPipeline = ({ triggerInfo, mode, score, position, config }) => {
  if (!triggerInfo) return null;

  const {
    active,
    distancePct,
    consecutiveSellDays = 0,
    sellDaysNeeded = 0,
    consecutiveBuyDays = 0,
    buyDaysNeeded = 0,
  } = triggerInfo;

  if (!isNum(distancePct)) return null;

  const isInvested = position === 'invested';
  const isCash = position === 'cash';
  const riskBoundary = config?.riskBoundary;
  const isConservative = mode === 'conservative';

  // Visual severity drives the container's left-border color.
  let severity = 'safe';
  if (active && isInvested && consecutiveSellDays > 0) severity = 'confirming';
  else if (active) severity = 'active';

  const containerClass = [
    styles.triggerPipeline,
    severity === 'active' ? styles.triggerPipelineActive : '',
    severity === 'confirming' ? styles.triggerPipelineConfirming : '',
  ].filter(Boolean).join(' ');

  // ── Cash position ──────────────────────────────────────────────────────
  if (isCash) {
    let cashContent;
    if (active) {
      cashContent = (
        <span className={styles.triggerGateActive}>
          Price gate still active — waiting for conditions to clear
        </span>
      );
    } else if (consecutiveBuyDays > 0 && buyDaysNeeded > 0 && consecutiveBuyDays >= buyDaysNeeded) {
      cashContent = (
        <span className={styles.triggerConfirmation}>
          Buy signal confirmed — returning to Invested
        </span>
      );
    } else if (consecutiveBuyDays > 0) {
      cashContent = (
        <span className={styles.triggerConfirmation}>
          Buy confirmation: day{' '}
          <span className={styles.triggerConfirmProgress}>{consecutiveBuyDays}</span>
          {' '}of {buyDaysNeeded}
        </span>
      );
    } else {
      // Cash, trigger inactive, no buy days accumulating. The gate status now
      // lives in the ModelState headline, so the pipeline shows only the
      // proximity bar here (no redundant text line).
      cashContent = null;
    }

    return (
      <div className={containerClass} data-testid="trigger-pipeline">
        {cashContent && <div className={styles.triggerLine}>{cashContent}</div>}
        <ProximityBar distancePct={distancePct} />
      </div>
    );
  }

  // ── Invested position ──────────────────────────────────────────────────
  // Gate status (active/inactive + distance) now lives in the ModelState
  // headline row. The pipeline shows only the confirmation status (when the
  // gate is active) and the proximity bar below.

  // Confirmation line (only when trigger is active): confirmation status.
  let confirmLine = null;
  if (active) {
    if (isConservative) {
      // Conservative: show only generic sell confirmation when confirming;
      // never reveal the voting mechanism (condition count / threshold).
      if (consecutiveSellDays > 0) {
        confirmLine = (
          <span className={styles.triggerConfirmation}>
            Sell confirmation: day{' '}
            <span className={styles.triggerConfirmProgress}>{consecutiveSellDays}</span>
            {' '}of {sellDaysNeeded}
          </span>
        );
      }
      // else: confirmLine stays null — "Price gate active" via gateLine is enough.
    } else if (isNum(score) && isNum(riskBoundary) && score >= riskBoundary) {
      confirmLine = (
        <span className={styles.triggerConfirmation}>
          Sell confirmation: day{' '}
          <span className={styles.triggerConfirmProgress}>{consecutiveSellDays}</span>
          {' '}of {sellDaysNeeded}
        </span>
      );
    } else {
      confirmLine = (
        <span className={styles.triggerMuted}>
          Below risk threshold — no sell signal forming
        </span>
      );
    }
  }

  return (
    <div className={containerClass} data-testid="trigger-pipeline">
      {confirmLine && <div className={styles.triggerLine}>{confirmLine}</div>}
      <ProximityBar distancePct={distancePct} />
    </div>
  );
};

export default TriggerPipeline;
